import { isDefined, isEmpty, isNonEmpty, isNotDefined } from '@sgme/fp';
import clsx from 'clsx';
import { type FocusEvent, type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { isNotEmpty } from '@/components/sg-design-system/utils';

import type { Color } from '../../../types/colors.ts';
import type { MaterialIcon } from '../../../types/icons.ts';
import type { Size } from '../../../types/layout';
import { SgIcon } from '../../display/SgIcon';
import { SgTooltip } from '../../feedback/SgTooltip';

export type SgInputProps = {
  value?: string | number;
  placeholder?: string;
  valuePosition?: 'start' | 'end';
  // maxDecimals?: number;

  unit?: string;
  unitIcon?: MaterialIcon;
  unitBackgroundColor?: Color;
  unitColor?: Color;
  unitPosition?: 'start' | 'end';

  readOnly?: boolean;
  disabled?: boolean;
  tabNoIndex?: boolean;

  className?: string;

  // locale?: Locale;
  // integerOnly?: boolean;
  // allowNegative?: boolean;
  // acceleratorsEnabled?: boolean;
  selectAllOnFocus?: boolean;

  size?: Size;
  state?: 'default' | 'edited' | 'valid' | 'invalid' | 'warning';

  validationMessage?: string;
  validationDisplay?: 'aside' | 'tooltip';
  validationTooltipPosition?: 'top' | 'bottom' | 'left' | 'right';

  ref?: RefObject<HTMLInputElement | null>;
  'data-e2e'?: string;

  onChange?: (value: string) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  onDirty?: (isDirty: boolean) => void;
  onUnitClick?: () => void;
};

export const SgInput = (props: SgInputProps) => {
  const {
    value,
    placeholder,
    valuePosition = props.unitPosition ?? 'start',
    // maxDecimals,
    unit,
    unitIcon,
    unitBackgroundColor = 'discreet-primary',
    unitColor,
    unitPosition = 'start',
    onChange,
    onFocus,
    onBlur,
    onDirty,
    onUnitClick,
    readOnly,
    disabled,
    validationMessage,
    validationDisplay = 'aside',
    validationTooltipPosition = 'top',
    ref,
    tabNoIndex,
    // locale = 'en',
    // acceleratorsEnabled,
    selectAllOnFocus = false,
    // allowNegative = true,
    // integerOnly = false,
    size = 'md',
    state = 'default',
    className,
    'data-e2e': dataE2e,
  } = props;

  // CHANGED: simple format
  const formatValue = useCallback((value: number | string): string => {
    return `${value}`;
  }, []);

  const [inputValue, setInputValue] = useState(() => formatValue(value ?? ''));

  const [focusValue, setFocusValue] = useState(value ?? '');
  const [isDirty, setIsDirty] = useState(false);
  const inputElementRef = useRef<HTMLInputElement | null>(null);

  // Synchronise inputValue with value prop
  useEffect(() => {
    setInputValue(formatValue(value ?? ''));
  }, [value, formatValue]);

  // Dirty state management
  useEffect(() => {
    if (onDirty) {
      onDirty(isDirty);
    }
  }, [isDirty, onDirty]);

  // Focus management
  const handleInputFocus = (event: FocusEvent<HTMLInputElement>) => {
    setFocusValue(value ?? '');
    setIsDirty(false);

    if (selectAllOnFocus) {
      event.target.select();
    }

    if (onFocus) {
      onFocus(event);
    }
  };

  // Blur management
  const handleInputBlur = () => {
    if (onBlur) {
      onBlur();
    }

    if (isDirty) {
      // CHANGED: no need to parse
      onChange?.(inputValue);

      setIsDirty(false);
    }
  };

  // Change management
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const parsedValue = formatValue(rawValue);

    setInputValue(parsedValue);
    setIsDirty(parsedValue !== focusValue);
  };

  // Keyboard management
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      inputElementRef.current?.blur();
      return;
    }

    if (event.key === 'Escape') {
      setInputValue(formatValue(focusValue));
      setIsDirty(false);

      event.preventDefault();

      return;
    }

    // CHANGED: no accelerator management

    // CHANGED: whitelist is not useful here

    // if (['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'End', 'Home'].includes(event.key)) {
    //   // allowed
    //   return;
    // }
    //
    // if (event.ctrlKey && ['a', 'v', 'c', 'x'].includes(event.key)) {
    //   // ctrl + a/v/c/x allowed
    //   return;
    // }

    // if the key is not allowed (0-9 . ,), prevent the input
    // const isNumberKey = /^[0-9]$/.test(event.key);
    // const { decimalSeparator } = getSeparatorsFromLocale(locale);
    // const isDecimalKey = event.key === decimalSeparator && !integerOnly;
    // const isNegativeKey = event.key === '-' && allowNegative;
    //
    // if (!isNumberKey && !isDecimalKey && !isNegativeKey) {
    //   event.preventDefault();
    // }
  };

  // Clipboard management
  const handleInputCopy = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.clipboardData.setData('text/plain', formatValue(inputValue));

    event.preventDefault();
  };

  const handleInputCut = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.clipboardData.setData('text/plain', formatValue(inputValue));

    setInputValue('');
    setIsDirty(true);

    event.preventDefault();
  };

  const handleInputPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedValue = event.clipboardData.getData('text');

    const parsedValue = formatValue(pastedValue);

    setInputValue(parsedValue);
    setIsDirty(parsedValue !== focusValue);

    event.preventDefault();
  };

  // Accessibility
  const ariaInvalid = state === 'invalid' ? true : undefined;

  const unitClassName = clsx('input-group-text', {
    [`text-${unitColor}`]: isNotEmpty(unitColor),
    'text-small fs-12': size === 'sm',
    'text-medium fs-14': size === 'md',
    'text-large fs-16': size === 'lg',
    'text-xlarge fs-18': size === 'xl',
    'border-bottom border-info': state === 'edited',
    'border-bottom border-success': state === 'valid',
    'border-bottom border-danger': state === 'invalid',
    'border-bottom border-warning': state === 'warning',
  });

  const unitElement =
    isNotEmpty(unit) || isNotEmpty(unitIcon) ? (
      isDefined(onUnitClick) ? (
        <button
          data-e2e={isDefined(dataE2e) ? `${dataE2e}-unit` : undefined}
          onClick={onUnitClick}
          disabled={disabled}
          className={clsx(unitClassName, 'btn', `btn-${unitBackgroundColor}`)}
          type="button"
          // tabIndex={unitTabNoIndex ? -1 : undefined}
        >
          {isNotEmpty(unitIcon) ? <SgIcon icon={unitIcon} size={size} /> : unit}
        </button>
      ) : (
        <span
          data-e2e={isDefined(dataE2e) ? `${dataE2e}-unit` : undefined}
          className={clsx(unitClassName, `bg-${unitBackgroundColor}`)}
        >
          {isNotEmpty(unitIcon) ? <SgIcon icon={unitIcon} size={size} /> : unit}
        </span>
      )
    ) : null;

  const input = (
    <input
      data-e2e={`${dataE2e}-input`}
      type="text"
      value={inputValue}
      readOnly={readOnly}
      disabled={disabled}
      ref={(node) => {
        if (ref !== undefined) {
          ref.current = node;
        }
        inputElementRef.current = node;
      }}
      className={clsx(className, `form-control form-control-${size}`, {
        'is-edited': state === 'edited',
        'is-valid': state === 'valid',
        'is-invalid': state === 'invalid',
        'is-warning': state === 'warning',
        'text-start': valuePosition === 'start',
        'text-end': valuePosition === 'end',
      })}
      placeholder={placeholder}
      tabIndex={tabNoIndex ? -1 : 0}
      aria-invalid={ariaInvalid}
      onFocus={handleInputFocus}
      onBlur={handleInputBlur}
      onChange={handleInputChange}
      onKeyDown={handleInputKeyDown}
      onCopy={handleInputCopy}
      onCut={handleInputCut}
      onPaste={handleInputPaste}
    />
  );

  const inputGroup = (
    <div
      className={clsx('input-group', className, {
        'input-group-sm': size === 'sm',
        'input-group-md': size === 'md',
        'input-group-lg': size === 'lg',
        'input-group-xl': size === 'xl',
      })}
      data-e2e={dataE2e}
    >
      {unitPosition === 'start' && unitElement}
      {input}
      {unitPosition === 'end' && unitElement}
    </div>
  );

  if (isNotDefined(validationMessage) || isEmpty(validationMessage)) {
    return inputGroup;
  }

  return isNonEmpty(validationMessage) && validationDisplay === 'aside' ? (
    <div>
      {inputGroup}
      <div className="d-block invalid-feedback">{validationMessage}</div>
    </div>
  ) : (
    <SgTooltip mode="hover" backgroundColor="danger" arrow={true} placement={validationTooltipPosition}>
      <SgTooltip.Trigger>{inputGroup}</SgTooltip.Trigger>
      <SgTooltip.Content>
        <div className="p-1">{validationMessage}</div>
      </SgTooltip.Content>
    </SgTooltip>
  );
};
